import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getInitials(fullName: string): string {
  return fullName
    .split(' ') // Split the name into an array of words
    .map((word) => word.charAt(0).toUpperCase()) // Take the first character of each word and capitalize it
    .join('') // Join the initials together without spaces
}

/**
 * Calculates the relative luminance of a color to determine appropriate text color contrast.
 * Uses the WCAG formula for relative luminance.
 * @param color - A CSS color string (hex, rgb, rgba, or named color)
 * @returns 'black' or 'white' depending on which provides better contrast
 */
export function getContrastTextColor(color: string): '#080808' | 'white' {
  // Convert color to RGB values
  let r = 0,
    g = 0,
    b = 0

  // Handle hex colors (#RGB or #RRGGBB)
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16)
      g = parseInt(hex[1] + hex[1], 16)
      b = parseInt(hex[2] + hex[2], 16)
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16)
      g = parseInt(hex.substring(2, 4), 16)
      b = parseInt(hex.substring(4, 6), 16)
    }
  }
  // Handle rgb() or rgba() format
  else if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g)
    if (match) {
      r = parseInt(match[0])
      g = parseInt(match[1])
      b = parseInt(match[2])
    }
  }
  // For named colors or other formats, try to use a temporary element
  else {
    // Create a temporary element to get computed color
    const temp = document.createElement('div')
    temp.style.color = color
    document.body.appendChild(temp)
    const computed = window.getComputedStyle(temp).color
    document.body.removeChild(temp)

    const match = computed.match(/\d+/g)
    if (match) {
      r = parseInt(match[0])
      g = parseInt(match[1])
      b = parseInt(match[2])
    }
  }

  // Calculate relative luminance using WCAG formula
  const rsRGB = r / 255
  const gsRGB = g / 255
  const bsRGB = b / 255

  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4)

  const luminance = 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear

  // Return black for light backgrounds, white for dark backgrounds
  // Threshold of 0.5 works well in practice, but you can adjust it
  return luminance > 0.5 ? '#080808' : 'white'
}

/**
 * Validates whether a string is a valid hexadecimal color code.
 *
 * Supports the following hex color formats:
 * - 3-digit hex: #RGB (e.g., #fff, #a0b)
 * - 4-digit hex: #RGBA (e.g., #ffff, #a0bf)
 * - 6-digit hex: #RRGGBB (e.g., #ffffff, #a0b1c2)
 * - 8-digit hex: #RRGGBBAA (e.g., #ffffff80, #a0b1c2ff)
 *
 * @param color - The string to validate as a hex color code
 * @returns `true` if the string is a valid hex color code, `false` otherwise
 *
 * @example
 * ```typescript
 * isValidHexColor("#fff");       // returns true
 * isValidHexColor("#ffffff");    // returns true
 * isValidHexColor("#ff00ff80");  // returns true
 * isValidHexColor("ffffff");     // returns false (missing #)
 * isValidHexColor("#gggggg");    // returns false (invalid characters)
 * ```
 */
export function isValidHexColor(color: string): boolean {
  // Regular expression to match valid hex color codes
  // Matches #RGB, #RRGGBB, #RGBA, and #RRGGBBAA formats
  const hexColorRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/

  return hexColorRegex.test(color)
}

/**
 * Converts a hex color code to HSLA format with specified alpha opacity.
 * If the input is not a valid hex color code, returns the original string unchanged.
 *
 * @param color - The color string to convert (hex format or any other string)
 * @param alpha - The alpha opacity value (0 to 1). Defaults to 1.0 (fully opaque)
 * @returns The color in HSLA format with specified opacity if input is valid hex, otherwise the original string
 *
 * @example
 * ```typescript
 * hexToHslaOrPassthrough("#ff0000");        // returns "hsla(0, 100%, 50%, 1)"
 * hexToHslaOrPassthrough("#ff0000", 0.1);   // returns "hsla(0, 100%, 50%, 0.1)"
 * hexToHslaOrPassthrough("#fff", 0.5);      // returns "hsla(0, 0%, 100%, 0.5)"
 * hexToHslaOrPassthrough("rgb(0,0,0)");     // returns "rgb(0,0,0)"
 * hexToHslaOrPassthrough("invalid", 0.3);   // returns "invalid"
 * ```
 */
export function hexToHslaOrPassthrough(color: string, alpha: number = 1.0): string {
  // Check if the input is a valid hex color
  if (!isValidHexColor(color)) {
    return color
  }

  // Remove the # symbol
  let hex = color.slice(1)

  // Convert 3-digit or 4-digit hex to 6-digit or 8-digit
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  // Parse RGB values
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  // Calculate HSL
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / delta + 2) / 6
        break
      case b:
        h = ((r - g) / delta + 4) / 6
        break
    }
  }

  // Convert to degrees and percentages
  const hDeg = Math.round(h * 360)
  const sPercent = Math.round(s * 100)
  const lPercent = Math.round(l * 100)

  // Clamp alpha value between 0 and 1
  const clampedAlpha = Math.max(0, Math.min(1, alpha))

  return `hsla(${hDeg}, ${sPercent}%, ${lPercent}%, ${clampedAlpha})`
}

/**
 * Converts camelCase or CapitalCase strings to Sentence case.
 *
 * The function identifies word boundaries by capital letters and converts the string
 * so that only the first letter is capitalized, with the rest in lowercase.
 *
 * @param str - The string to convert (camelCase, CapitalCase, or any string)
 * @returns The string converted to sentence case
 *
 * @example
 * ```typescript
 * toSentenceCase("camelCase");           // returns "Camel case"
 * toSentenceCase("CapitalCase");         // returns "Capital case"
 * toSentenceCase("thisIsALongString");   // returns "This is a long string"
 * toSentenceCase("HTMLParser");          // returns "HTML parser"
 * toSentenceCase("IOError");             // returns "IO error"
 * toSentenceCase("already lowercase");   // returns "Already lowercase"
 * ```
 */
export function toSentenceCase(str: string): string {
  // Handle empty strings
  if (!str) {
    return str
  }

  // Insert a space before each capital letter (except the first character)
  // and convert to lowercase
  const spaced = str.replace(/([A-Z])/g, (match, letter, offset) => {
    // Don't add space before the first character
    return offset === 0 ? letter : ` ${letter}`
  })

  // Capitalize the first letter and lowercase the rest
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}
